import { BlogPost } from "./types";

export const comoReducirImagenParaEmail: BlogPost = {
  slug: "como-reducir-imagen-para-email",
  title: "Cómo reducir imágenes para email: tamaños, formatos y buenas prácticas",
  description: "Guía completa para optimizar imágenes antes de enviarlas por email. Tamaños recomendados, límites de adjuntos, cuándo adjuntar vs incrustar, y cómo evitar filtros de spam.",
  date: "2026-03-24",
  readTime: "9 min",
  keywords: "reducir imagen para email, comprimir imagen correo electrónico, tamaño imagen email, adjuntar foto email, imagen email sin perder calidad",
  content: `
## El problema de las imágenes en el email

Cuando envías un email con imágenes adjuntas, te enfrentas a varios desafíos técnicos que afectan tanto si el mensaje llega como si se ve correctamente:

- **Límites de adjuntos:** Gmail permite hasta 25MB por email, Outlook hasta 20MB, pero muchos servidores corporativos aceptan solo 5-10MB.
- **Filtros de spam:** emails con imágenes de gran tamaño activan sistemas antispam.
- **Descarga en móvil:** un adjunto de 8MB en una red móvil puede tardar bastante en descargar.
- **Almacenamiento:** en bandejas con cuota limitada, un email de 20MB ocupa el equivalente a miles de emails normales.

La regla práctica en email marketing profesional: imágenes de 50-150KB son ideales; adjuntos personales de 1-3MB son aceptables; nunca superes los 10MB si puedes evitarlo.

## Diferencia entre adjuntar e incrustar imágenes

### Imágenes adjuntas (attachments)

Las imágenes adjuntas viajan como archivos separados al cuerpo del email. El destinatario las ve como archivos descargables. Son la opción estándar cuando envías fotos personales o documentos fotográficos.

**Cuándo usarlas:** fotos personales, documentos fotográficos, imágenes de trabajo que el destinatario necesita guardar o imprimir.

### Imágenes incrustadas (inline/embedded)

Las imágenes incrustadas forman parte visual del cuerpo del email. Se usan en newsletters y emails de marketing.

**Cuándo usarlas:** newsletters, emails comerciales, firma de email.

**Importante:** las imágenes en newsletters modernas se sirven desde un servidor (URL), no van literalmente dentro del archivo de email. Cuando Mailchimp dice "incrustar imagen", sube el archivo a sus servidores y referencia la URL.

## Tamaños recomendados por tipo de email

### Email personal con fotos

| Situación | Tamaño recomendado | Peso objetivo |
|---|---|---|
| 1-3 fotos casuales | 1200px de ancho, JPEG 80% | 200-400KB cada una |
| 4-10 fotos (evento, viaje) | 1000px de ancho, JPEG 75% | 150-300KB cada una |
| Foto de documento para trámites | 1200px de ancho, JPEG 88% | 200-400KB |
| Foto de producto para vender | 1000-1200px, JPEG 85% | 200-350KB |

Para un email con 5-6 fotos de vacaciones, apunta a que el total de adjuntos no supere 5MB.

### Newsletter y email marketing

| Tipo de imagen | Ancho máximo | Peso objetivo |
|---|---|---|
| Imagen hero/cabecera | 600px | 80-120KB |
| Imagen de producto | 300-500px | 40-80KB |
| Logo en cabecera | 200px | 10-20KB |
| Imagen de firma | 150px | 15-30KB |

El ancho de 600px es estándar en email marketing porque los clientes de email suelen mostrar el contenido en un contenedor de 600px de ancho.

### Firma de email profesional

La imagen en la firma tiene requerimientos especiales:
- **Peso máximo:** 20-30KB — firmas más pesadas retrasan la carga
- **Dimensiones:** logo máximo 200×60px, foto de perfil máximo 100×100px
- **Formato:** PNG para logos (bordes nítidos), JPEG para fotos de perfil

Una firma con imagen de 50KB se envía con cada email. Si envías 100 emails al día, eso son 5MB diarios solo en la firma.

## Cómo reducir imágenes para adjuntar

### Proceso para fotos de móvil

Las fotos de smartphone moderno (12-50MP) necesitan reducción significativa:

1. Abre la foto en nuestro [compresor de imágenes](/)
2. Activa redimensionado: selecciona ancho máximo de 1200px para uso general
3. Establece calidad JPEG al 80%
4. Comprueba el peso resultante — debe estar en 150-350KB
5. Descarga y adjunta

Una foto de móvil típica de 5MB a 4000×3000px quedará en 180-280KB tras este proceso.

### Proceso para fotos de cámara profesional

Las fotos RAW convertidas o JPEG de alta resolución pueden superar los 20MB:

1. Redimensiona a 1600px de ancho máximo
2. Comprime al 80-85% JPEG
3. El resultado estará entre 300-600KB — perfectamente adjuntable

Si el destinatario necesita la imagen de alta resolución para imprimir, considera enviarla por WeTransfer o Google Drive.

### Proceso para documentos fotográficos

Fotos de contratos, facturas, DNI, o cualquier imagen con texto:

1. Redimensiona a máximo 1600px de ancho (para textos pequeños, no reduzcas más)
2. Comprime al 85-88% JPEG — necesitas algo más de calidad para legibilidad del texto
3. Verifica que el texto sea legible en el resultado antes de enviar

## Envío de múltiples imágenes

### Adjuntar individualmente (hasta 5-6 fotos)

Si son pocas fotos y cada una pesa menos de 500KB, adjuntar individualmente es lo más sencillo.

### Comprimir en ZIP

Para 10 o más imágenes, comprimir en un archivo ZIP:
- Organiza las imágenes en una carpeta
- Comprímela (clic derecho → "Comprimir" o "Enviar a ZIP")
- Adjunta el ZIP único

Nota: el ZIP no comprimirá mucho las imágenes JPEG (ya están comprimidas). El beneficio es organizativo, no de tamaño.

### Servicio de transferencia para colecciones grandes

Para álbumes completos de eventos o entrega de proyectos fotográficos:
- WeTransfer (gratis hasta 2GB por envío)
- Google Drive (comparte enlace)
- Dropbox (comparte enlace de carpeta)

## Impacto en la entregabilidad del email

En email marketing, el tamaño de las imágenes afecta directamente si tu email llega a la bandeja de entrada o al spam:

### Señales que activan filtros de spam

- Proporción imagen/texto muy alta (más del 60% del email es imagen)
- Imágenes de gran tamaño que no se sirven desde CDN
- Muchas imágenes sin texto alternativo (atributo alt)
- Peso total del email superior a 100KB

### Buenas prácticas de email marketing con imágenes

- Texto alternativo en todas las imágenes (atributo \`alt\` descriptivo)
- Balance imagen/texto: mínimo 60% texto, máximo 40% imágenes
- Imágenes servidas desde CDN (Mailchimp, Mailrelay, HubSpot lo hacen automáticamente)
- Imágenes con peso individual de 50-100KB para web, 20-40KB para móvil

## Preguntas frecuentes

### ¿Cuánto es demasiado grande para adjuntar a un email?

La regla práctica: un único email no debería superar 10MB en total, y es mejor apuntar a menos de 5MB. Más allá de 10MB, los servidores corporativos frecuentemente rechazan el mensaje o lo mandan a spam.

### ¿Por qué las imágenes que envío por email se ven pixeladas al abrirlas en el ordenador?

Probablemente las redujiste demasiado (menos de 800px de ancho para una imagen que se verá en pantalla completa) o aplicaste compresión muy agresiva (por debajo del 60% JPEG). Para imágenes que deben verse bien en pantalla, mantén mínimo 1000px de ancho y calidad JPEG del 75-80%.

### ¿Las imágenes incrustadas en el cuerpo del email cuentan para el límite de adjuntos?

Técnicamente sí, si las incrustas directamente. Si la imagen está hospedada en un servidor externo (URL) — como en las newsletters de Mailchimp — no cuenta para el tamaño del email.

### ¿Cómo envío fotos en calidad original por email?

El email en sí no comprime los archivos adjuntos. Si la imagen llega con peor calidad, es porque el sistema que usas la comprimió al adjuntarla. En iPhone Mail, cuando adjuntas fotos, elige "Tamaño real" en el cuadro de diálogo para evitar la compresión automática.

### ¿Es mejor JPEG o PNG para imágenes en emails?

Para fotografías: siempre JPEG, que es entre 5-15 veces más pequeño que PNG para el mismo contenido fotográfico. Para logos con bordes nítidos: PNG. WebP es técnicamente mejor en peso, pero algunos clientes de email antiguos no lo muestran correctamente.

## Conclusión

Para emails personales con fotos adjuntas, la combinación de redimensionar a 1000-1200px y comprimir al 80% JPEG produce resultados de calidad excelente con pesos de 150-300KB por imagen — perfectos para adjuntar sin problemas en cualquier cliente de correo.

Para email marketing y newsletters, las imágenes deben ser más agresivamente optimizadas (50-100KB) y siempre con texto alternativo para no depender de que el cliente de email cargue las imágenes.

Usa nuestro [compresor de imágenes](/) para el proceso completo.
  `
};
